<template>
    <card title="Inventory">
        <b-row align-v="end">
            <b-col cols="8">
                <b-row>
                    <b-col>
                        <SearchComponent v-model="store" label="Store" search-u-r-l="/store" prepopulate clearable />
                    </b-col>
                    <b-col>
                        <SearchComponent v-model="program" label="Program" prepopulate :params="{}"
                            search-u-r-l="/Program" clearable />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="2">
                        <label>DEA Class</label>
                    </b-col>
                    <b-col>
                        <checkbox v-model="deaSelection[0]" inline>I</checkbox>
                        <checkbox v-model="deaSelection[1]" inline>II</checkbox>
                        <checkbox v-model="deaSelection[2]" inline>III</checkbox>
                        <checkbox v-model="deaSelection[3]" inline>IV</checkbox>
                        <checkbox v-model="deaSelection[4]" inline>V</checkbox>
                        <checkbox v-model="deaSelection[5]" inline>VI</checkbox>
                    </b-col>
                </b-row>
            </b-col>
            <b-col cols="2" class="pt-2">
                <b-dropdown variant="success" text="Export...">
                    <b-dropdown-item @click="savePDF">
                        <i class="fa fa-fw fa-lg fa-file-pdf" />
                        PDF
                    </b-dropdown-item>
                    <b-dropdown-item @click="saveCSV">
                        <i class="fa fa-fw fa-lg fa-file-csv" />
                        CSV
                    </b-dropdown-item>
                </b-dropdown>
            </b-col>
            <b-col cols="2">
                <b-button variant="danger" :block="true" @click="clearClicked">
                    Reset Filters
                </b-button>
            </b-col>
        </b-row>
        <b-row id="reports-row">
            <b-col>
                <b-row v-show="isLoading || reportItems.length" class="py-3">
                    <b-col>
                        <b-overlay :show="isLoading" no-center>
                            <b-table id="report-table" striped outlined selectable responsive="true"
                                :items="reportItems" :fields="reportFields" :sort-by="sortBy" selected-variant="warning"
                                :per-page="pageSize" :current-page="pageNumber" />
                            <BottomPagerBar v-model="pageNumber" :total-rows="reportItems?.length" :per-page="pageSize"
                                show-total :loading="isLoading" />
                        </b-overlay>
                    </b-col>
                </b-row>
            </b-col>
        </b-row>
    </card>
</template>

<script lang="ts">
import { Component, Mixins, Watch } from 'vue-property-decorator';
import Checkbox from "@/components/Inputs/Checkbox.vue";
import SearchComponent from '@/components/SearchComponent.vue';
import axios from 'axios';
import { ReportExporter } from '@/mixins/ReportExporter';
import { Store } from "@/models/Store";
import { Program } from "@/models/Program";
import BottomPagerBar from '@/components/BottomPagerBar.vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

@Component({
    name: "InventoryReportPage",
    components: { Checkbox, SearchComponent, BottomPagerBar },
})
export default class InventoryReportPage extends Mixins(ReportExporter) {

    private store: Store | null = new Store();
    private program: Program | null = new Program();
    private deaSelection: boolean[] = [true, true, true, true, true, true];
    private sortBy: any = null;
    private reportItems: any[] = [];
    private pageNumber = 1;
    private pageSize = 100;
    private isLoading = false;

    created() {
        console.log("%c Created InventoryReportPage", "color: green;");
        this.loadHasInventoryLocations();
    }

    beforeDestroy() {
        console.log('%c Destroying InventoryReportPage', 'color: red');
    }

    get reportFields() {
        return [
            { label: "NDC", key: "ndc" },
            { label: "Drug Name", key: "drugName" },
            { label: "Quantity", key: "quantity" },
            { label: "Allocated", key: "allocated" },
            { label: "Unit of Measure", key: "billingUnit" },
            { label: "DEA Schedule", key: "deaClassId" },
            { label: "Store", key: "storeName" },
            { label: "Program", key: "programName" },
            { label: "Location", key: "location.name", hidden: !this.hasInventoryLocations }
        ].filter(f => f.hidden !== true);
    }

    get hasFilters() {
        return this.store?.id || this.program?.id;
    }

    @Watch('store')
    @Watch('program')
    @Watch('deaSelection')
    async getReport() {
        if (!this.hasFilters) {
            this.reportItems = [];
            return;
        }

        const config = {
            params: {
                storeId: this.store ? this.store.id : 0,
                programId: this.program ? this.program.id : 0,
                deaClasses: this.deaSelection.map(selection => selection ? 1 : 0).join()
            }
        };
        const reportPath = `/inventory/report`;

        try {
            this.isLoading = true;
            const response = await axios.get(reportPath, config);

            this.reportItems = response.data;
            this.sortBy = this.reportFields[0].key;

            const deaSelectionEmpty = !this.deaSelection.includes(true);
            if (deaSelectionEmpty) {
                for (let i = 0; i < 6; i++)
                    this.deaSelection[i] = true;
            }
        } catch (error) {
            console.warn(error);
            this.$notification(NotificationOptions.error(error));
        }

        this.isLoading = false;
    }

    async savePDF() {
        const currentPageSize = this.pageSize;
        this.pageSize = this.reportItems.length;
        await this.ExportPDF("Inventory Report", "#report-table");
        this.pageSize = currentPageSize;
    }

    saveCSV() {
        this.ExportCSV("Inventory Report", this.reportItems, this.reportFields);
    }

    clearClicked() {
        this.store = new Store();
        this.program = new Program();
        this.deaSelection = [true, true, true, true, true, true];
    }

    protected hasInventoryLocations = false;

    async loadHasInventoryLocations() {
        try {
            this.hasInventoryLocations = (await axios.get<boolean>("/inventory/has-inventory-locations")).data;
            this.hasInventoryLocations = false;
        } catch {
            //ignore
        }
    }
}
</script>
